import { useState } from 'react';

import { Box, Button, CircularProgress, Divider, Tooltip } from '@mui/material';

import { Bbar } from 'components/docked';
import { ConfirmModal } from 'components/modal';
import { CurrencyProgressText, CurrencyText, WeightProgressText, WeightText } from 'components/text';
import { usePermissionsContext } from 'context/PermissionsContext';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { CarrinhoBbarTitle } from 'pages/compra/carrinho/components';
import { useCarrinhoContext } from 'pages/compra/carrinho/contexts';
import { useCarrinhoBbar, useFinishPedido } from 'pages/compra/carrinho/hooks';

const CarrinhoBbarContainer = ({ children }: { children: React.ReactNode }) => {
    return (
        <Box
            sx={{
                width: { xs: '100%', md: 'auto' },
                display: { xs: 'grid', md: 'flex' },
                gridTemplateColumns: { xs: '84px 2fr 2fr' },
                alignItems: 'center',
                gap: { xs: 1.25, md: 2.5 },
            }}
        >
            {children}
        </Box>
    );
};

export function CarrinhoBbar() {
    const [openFinishModal, setOpenFinishModal] = useState<boolean>(false);
    const { finishAllPedidos, finishingPedidosCount } = useFinishPedido();
    const { hasPermission } = usePermissionsContext();
    const { isUpMd } = useBreakpoints();
    const { selectedPedidos, pedidos } = useCarrinhoContext();
    const {
        configMinimo,
        totalizador,
        selectionVlTotal,
        selectionVlPeso,
        pePeso,
        peValor,
        disableFinalizarAll,
        tooltipTitle,
        dtEntregaLabel,
        isFetching,
    } = useCarrinhoBbar();

    const hasSelection = selectedPedidos.length > 0;
    const hasVlPesoTotal = typeof totalizador?.vlPesoLiquidoTotal === 'number';
    const hasVlTotal = typeof totalizador?.vlTotal === 'number';
    const hasMinimo = Boolean(configMinimo?.vlPesoTotalPedidoMinimo || configMinimo?.vlTotalPedidoMinimo);
    const hasGeral = hasVlPesoTotal || hasVlTotal;
    const hasPedidoTotalizador = selectedPedidos[0]?.configPedido.idTipoPedido === 4; // 4 = Totalizador

    return (
        <>
            <ConfirmModal
                open={openFinishModal}
                setOpen={setOpenFinishModal}
                confirm={finishAllPedidos}
                title={hasSelection ? 'Finalizar pedidos selecionados' : 'Finalizar todos os pedidos'}
                text={
                    hasSelection ? 'Tem certeza que deseja finalizar os pedidos selecionados?' : 'Tem certeza que deseja finalizar todos os pedidos?'
                }
            />

            <Bbar sx={{ gap: { xs: 1.5, md: 2.5 } }}>
                {/* loading */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'space-between' }}>
                    {isFetching && finishingPedidosCount > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center' }}>
                            <CarrinhoBbarTitle primary title="Finalizando" />

                            <CarrinhoBbarTitle title={`${finishingPedidosCount} pedido(s)`} />
                        </Box>
                    )}

                    {isFetching && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1.5,
                            }}
                        >
                            <CircularProgress size={20} />
                        </Box>
                    )}
                </Box>

                {!isFetching && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 1.5, md: 2.5 },
                            flexDirection: { xs: 'column-reverse', md: 'row' },
                            width: { xs: '100%', md: 'auto' },
                        }}
                    >
                        {/* total geral dos pedidos listados */}
                        {hasGeral && (
                            <CarrinhoBbarContainer>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center' }}>
                                    <CarrinhoBbarTitle primary title={isUpMd ? 'Total no carrinho' : 'Carrinho'} />

                                    <CarrinhoBbarTitle title={`${pedidos.length} pedido(s)`} />
                                </Box>

                                {hasVlPesoTotal && <WeightText label="Peso" value={totalizador?.vlPesoLiquidoTotal} />}

                                {hasVlTotal && <CurrencyText label="Valor" value={totalizador?.vlTotal} />}
                            </CarrinhoBbarContainer>
                        )}

                        {hasGeral && hasMinimo && !hasPedidoTotalizador && (
                            <Divider flexItem orientation={isUpMd ? 'vertical' : 'horizontal'} sx={{ borderStyle: { xs: 'dashed', md: 'solid' } }} />
                        )}

                        {/* se tem seleção e mínimo e não for totalizador, exibe progress da seleção do currency e weight */}
                        {hasMinimo && hasSelection && !hasPedidoTotalizador && (
                            <CarrinhoBbarContainer>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center' }}>
                                    <CarrinhoBbarTitle
                                        primary
                                        title={`${selectedPedidos.length} selecionado${selectedPedidos.length > 1 ? 's' : ''}`}
                                    />

                                    {dtEntregaLabel && <CarrinhoBbarTitle title={isUpMd ? `Entrega ${dtEntregaLabel}` : dtEntregaLabel} />}
                                </Box>

                                {!!pePeso && (
                                    <WeightProgressText
                                        label="Peso"
                                        progress={pePeso}
                                        value={selectionVlPeso}
                                        target={configMinimo?.vlPesoTotalPedidoMinimo}
                                    />
                                )}

                                {!!peValor && (
                                    <CurrencyProgressText
                                        label="Valor"
                                        progress={peValor}
                                        value={selectionVlTotal}
                                        target={configMinimo?.vlTotalPedidoMinimo}
                                    />
                                )}
                            </CarrinhoBbarContainer>
                        )}

                        {/* se não tem seleçao e tem mínimos, exibe os valores mínimos */}
                        {hasMinimo && !hasSelection && (
                            <CarrinhoBbarContainer>
                                <CarrinhoBbarTitle title="Mínimo" />

                                {!!configMinimo?.vlPesoTotalPedidoMinimo && <WeightText label="Peso" value={configMinimo.vlPesoTotalPedidoMinimo} />}

                                {!!configMinimo?.vlTotalPedidoMinimo && <CurrencyText label="Valor" value={configMinimo.vlTotalPedidoMinimo} />}
                            </CarrinhoBbarContainer>
                        )}
                    </Box>
                )}

                {/* botão para finalizar selecionados ou todos */}
                {hasPermission(['COMPRA_PEDIDO_FINALIZAR']) && (
                    <Tooltip title={tooltipTitle}>
                        <span>
                            <Button
                                disabled={disableFinalizarAll}
                                variant="contained"
                                onClick={() => setOpenFinishModal(true)}
                                sx={{ width: { xs: '100%', md: '316px' } }}
                            >
                                {selectedPedidos.length > 0 ? 'Finalizar pedidos selecionados' : 'Finalizar todos os pedidos'}
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </Bbar>
        </>
    );
}
